import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getPageBySlug, getCollection, sortHoldstartDates, hasFixedDate } from "../lib/cms";
import ContactForm from "../components/ContactForm";
import { 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Sparkles, 
  ArrowRight, 
  Bus, 
  Compass, 
  ShieldCheck, 
  Users,
  CheckCircle
} from "lucide-react";

export default function Kontakt() {
  const [searchParams] = useSearchParams();
  const [pageData, setPageData] = useState(null);
  const [holdstartDates, setHoldstartDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Køreskolen i Gug");
  const [selectedHold, setSelectedHold] = useState("");

  useEffect(() => {
    const tabParam = (searchParams.get("tab") || searchParams.get("skole") || "").toLowerCase();
    if (tabParam.includes("sea")) {
      setActiveTab("SEA Efterskole");
    } else if (tabParam.includes("skipper")) {
      setActiveTab("Skipper Clement");
    } else if (tabParam.includes("kloster")) {
      setActiveTab("Klostermark Skolen");
    } else if (tabParam.includes("gug")) {
      setActiveTab("Køreskolen i Gug");
    }
  }, [searchParams]);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await getPageBySlug("kontakt");
        const dates = await getCollection("holdstart_dates");
        setPageData(page);
        setHoldstartDates(dates || []);
      } catch (err) {
        console.error("Error loading contact page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    }
  }, [pageData]);

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#tilmelding-form") {
        setTimeout(() => {
          const formElement = document.getElementById("tilmelding-form");
          if (formElement) {
            formElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    };

    if (!loading) {
      handleHashChange();
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [loading]);

  const selectHoldAndScroll = (holdTitle, holdTime, holdCategory) => {
    const hasCustomTime = holdTime && holdTime !== "Tidspunkter ikke fastsat";
    const categoryName = holdCategory || activeTab;
    const holdValue = `${holdTitle} — ${categoryName}${hasCustomTime ? ` (${holdTime})` : ""}`;
    setSelectedHold(holdValue);
    
    // Smooth scroll to form
    const formElement = document.getElementById("tilmelding-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="animate-pulse text-brand-primary font-bold text-lg">Henter holdstarts og tilmelding...</div>
      </div>
    );
  }

  // Group dates by category and sort by date
  const gugDates = sortHoldstartDates(holdstartDates.filter((d) => d.category === "Køreskolen i Gug" || d.category === "Almindelig"));
  const skipperDates = sortHoldstartDates(holdstartDates.filter((d) => d.category === "Skipper Clement"));
  const klostermarkDates = sortHoldstartDates(holdstartDates.filter((d) => d.category === "Klostermark Skolen"));
  const seaDates = sortHoldstartDates(holdstartDates.filter((d) => d.category === "SEA Efterskole"));

  const categories = {
    "Køreskolen i Gug": gugDates,
    "Skipper Clement": skipperDates,
    "Klostermark Skolen": klostermarkDates,
    "SEA Efterskole": seaDates,
  };

  return (
    <div className="bg-brand-light min-h-screen">
      
      {/* 1. Personal Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden border-b border-stone-200/50 bg-white">
        {/* Decorative background blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> Holdstart & Tilmelding
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-brand-dark tracking-tight leading-none">
                Tilmeld dig holdstart <br />
                <span className="text-brand-primary">hos Carina i Gug</span>
              </h1>
              <p className="text-stone-600 text-lg sm:text-xl leading-relaxed max-w-2xl">
                Er du klar til at tage kørekortet i en tryg, tålmodig og hyggelig atmosfære? 
                Hos mig i Gug er der altid kolde sodavand, lækre snacks og <strong className="font-bold text-brand-dark">maksimalt 8 elever pr. hold</strong>, 
                så der er god tid til dig. Find dit hold nedenfor og reserver din plads i dag!
              </p>

              {/* Quick Trust Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-stone-50 border border-stone-200/60">
                  <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-dark text-sm">Små hold</h4>
                    <p className="text-xs text-stone-500">Max 8 elever pr. hold</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-stone-50 border border-stone-200/60">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600 shrink-0">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-dark text-sm">Tryg kørsel</h4>
                    <p className="text-xs text-stone-500">100% tålmodighed</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-stone-50 border border-stone-200/60">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 shrink-0">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-dark text-sm">Automatgear</h4>
                    <p className="text-xs text-stone-500">Uden ekstra merpris</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Main Image */}
                <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-2xl aspect-[4/5] bg-stone-100">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/billede-af-mig-selv.jpg" 
                    alt="Carina Svensson - Din Kørelærer" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-transparent to-transparent"></div>
                  
                  {/* Image overlay text */}
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h3 className="font-black text-xl">Carina Svensson</h3>
                    <p className="text-xs text-brand-primary font-bold uppercase tracking-wider">Ejer & Kørelærer siden 2018</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. "Hvad sker der efter min tilmelding?" (Trin-for-trin guide) */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-brand-primary font-bold text-xs uppercase tracking-wider block mb-2">Gennemskuelig proces</span>
          <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight">
            Hvad sker der efter din tilmelding?
          </h2>
          <p className="text-stone-600 mt-3 text-sm sm:text-base">
            Det er utrolig nemt og trygt at tilmelde sig. Her er de 4 enkle trin i din tilmeldingsrejse:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
          {/* Connecting line for desktop */}
          <div className="hidden md:block absolute top-1/2 left-4 right-4 h-0.5 bg-stone-200 -translate-y-8 z-0"></div>

          {/* Step 1 */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm relative z-10 text-center flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-md mb-4">
              1
            </div>
            <h4 className="font-bold text-brand-dark text-base mb-2">Send tilmelding</h4>
            <p className="text-xs text-stone-500 leading-relaxed">
              Vælg dit ønskede hold nedenfor og udfyld formularen med dine oplysninger.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm relative z-10 text-center flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-md mb-4">
              2
            </div>
            <h4 className="font-bold text-brand-dark text-base mb-2">Bekræftelse på SMS</h4>
            <p className="text-xs text-stone-500 leading-relaxed">
              Jeg sender dig en personlig bekræftelse på SMS inden for 24 timer, så du ved, din plads er sikret.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm relative z-10 text-center flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-md mb-4">
              3
            </div>
            <h4 className="font-bold text-brand-dark text-base mb-2">Praktisk info på mail</h4>
            <p className="text-xs text-stone-500 leading-relaxed">
              Ca. 1-2 uger før holdstart modtager du en mail med alt praktisk info om lægeerklæring, pasfoto osv.
            </p>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm relative z-10 text-center flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-md mb-4">
              4
            </div>
            <h4 className="font-bold text-brand-dark text-base mb-2">Første teoriaften</h4>
            <p className="text-xs text-stone-500 leading-relaxed">
              Vi mødes i teorilokalet på Gugvej til en hyggelig og uformel introduktion med sodavand og snacks!
            </p>
          </div>
        </div>
      </section>

      {/* 3. Holdstart-sektion med Interaktiv Kategori-vælger (Tabs) */}
      <section className="py-12 bg-stone-50 border-y border-stone-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-brand-primary font-bold text-xs uppercase tracking-wider block mb-2">Find dit hold</span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight">
              Vælg din holdstart
            </h2>
            <p className="text-stone-600 mt-2 text-sm sm:text-base">
              Klik på kategorierne nedenfor for at se de kommende datoer. Klik på <strong className="font-semibold text-brand-dark">"Vælg dette hold"</strong> for at udfylde tilmeldingen med det samme.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex justify-center mb-10">
            <div className="bg-white p-1.5 rounded-2xl border border-stone-200 shadow-sm inline-flex flex-wrap justify-center gap-1">
              <button
                onClick={() => setActiveTab("Køreskolen i Gug")}
                className={`px-4 sm:px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 cursor-pointer ${
                  activeTab === "Køreskolen i Gug"
                    ? "bg-brand-primary text-white shadow-md"
                    : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                }`}
              >
                Køreskolen i Gug
              </button>
              <button
                onClick={() => setActiveTab("Skipper Clement")}
                className={`px-4 sm:px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 cursor-pointer ${
                  activeTab === "Skipper Clement"
                    ? "bg-brand-primary text-white shadow-md"
                    : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                }`}
              >
                Skipper Clement
              </button>
              <button
                onClick={() => setActiveTab("Klostermark Skolen")}
                className={`px-4 sm:px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 cursor-pointer ${
                  activeTab === "Klostermark Skolen"
                    ? "bg-brand-primary text-white shadow-md"
                    : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                }`}
              >
                Klostermark Skolen
              </button>
              <button
                onClick={() => setActiveTab("SEA Efterskole")}
                className={`px-4 sm:px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 cursor-pointer ${
                  activeTab === "SEA Efterskole"
                    ? "bg-brand-primary text-white shadow-md"
                    : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                }`}
              >
                SEA Efterskole
              </button>
            </div>
          </div>

          {/* Holdstart Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories[activeTab] && categories[activeTab].length > 0 ? (
              categories[activeTab].map((hold) => {
                const isFull = Boolean(
                  hold.is_fully_booked ||
                  (hold.status && (
                    hold.status.toLowerCase().includes("fuld") ||
                    hold.status.toLowerCase().includes("optaget") ||
                    hold.status.toLowerCase().includes("udsolgt")
                  ))
                );
                return (
                  <div
                    key={hold.id || hold.title}
                    className={`bg-white rounded-3xl border p-6 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md relative overflow-hidden ${
                      isFull ? "border-stone-200 opacity-80" : "border-stone-200 hover:border-brand-primary/40"
                    }`}
                  >
                    {/* Top status bar */}
                    <div className="flex justify-between items-center mb-6">
                      <span className="text-xs font-bold uppercase tracking-wider text-stone-400">
                        {hold.category || activeTab}
                      </span>
                      {isFull ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 text-xs font-bold border border-rose-200">
                          <XCircle className="h-3 w-3" /> FULD BOOKET
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200">
                          <CheckCircle2 className="h-3 w-3" /> Ledige pladser
                        </span>
                      )}
                    </div>

                    {/* Main Info */}
                    <div className="space-y-3 mb-8">
                      <div className="flex items-start gap-3">
                        <Calendar className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-black text-xl text-brand-dark leading-tight">{hold.title}</h4>
                          <p className="text-xs text-stone-500 mt-1">Holdstartsdato</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 pt-2">
                        <Clock className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-stone-700 font-medium leading-relaxed">
                            {hold.time && hold.time !== "Tidspunkter ikke fastsat"
                              ? hold.time
                              : hasFixedDate(hold.title)
                              ? "Oplyses ved tilmelding"
                              : "Tidspunkter ikke fastsat"}
                          </p>
                          <p className="text-xs text-stone-500 mt-0.5">Tidspunkt / Mødeplan</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      disabled={isFull}
                      onClick={() => selectHoldAndScroll(hold.title, hold.time, activeTab)}
                      className={`w-full py-3 px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer ${
                        isFull
                          ? "bg-stone-100 text-stone-400 cursor-not-allowed"
                          : "bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-white shadow-sm"
                      }`}
                    >
                      {isFull ? "Holdet er fuldt" : "Vælg dette hold"}
                      {!isFull && <ArrowRight className="h-4 w-4" />}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-12 bg-white rounded-3xl border border-stone-200">
                <Calendar className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 text-sm font-medium">Der er i øjeblikket ingen planlagte hold i denne kategori.</p>
                <p className="text-stone-400 text-xs mt-1">Kontakt Carina direkte for at høre om næste holdstart.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 5. Main Registration Section (Form & Info Grid) */}
      <section id="tilmelding-form" className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Contact Form */}
          <div className="lg:col-span-7">
            <ContactForm selectedHold={selectedHold} onHoldChange={setSelectedHold} />
          </div>

          {/* Right Column: Contact Details & Map */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* Practical Info Card */}
            <div className="bg-white rounded-3xl border border-stone-200 shadow-xl p-6 sm:p-8 space-y-6">
              <h3 className="text-2xl font-black text-brand-dark flex items-center gap-2">
                <Compass className="h-6 w-6 text-brand-primary" />
                Praktisk information
              </h3>
              
              <ul className="space-y-6 text-sm">
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-stone-50 border border-stone-200/60 flex items-center justify-center text-brand-primary shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-stone-400 block text-xs font-bold uppercase tracking-wider">Telefon</span>
                    <a href="tel:30517269" className="text-brand-dark hover:text-brand-primary font-black text-lg transition-colors">30 51 72 69</a>
                    <span className="text-stone-400 text-xs block mt-0.5">Send gerne en SMS, så kontakter jeg dig hurtigst muligt.</span>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-stone-50 border border-stone-200/60 flex items-center justify-center text-brand-primary shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-stone-400 block text-xs font-bold uppercase tracking-wider">E-mail</span>
                    <a href="mailto:carinaskoereskole@gmail.com" className="text-brand-dark hover:text-brand-primary font-black text-base transition-colors">carinaskoereskole@gmail.com</a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-stone-50 border border-stone-200/60 flex items-center justify-center text-brand-primary shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-stone-400 block text-xs font-bold uppercase tracking-wider">Teorilokale adresse</span>
                    <p className="text-brand-dark font-black text-base leading-tight">Gugvej 186 D, kld. tv.</p>
                    <p className="text-stone-600 text-sm">9210 Aalborg SØ</p>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-stone-50 border border-stone-200/60 flex items-center justify-center text-brand-primary shrink-0">
                    <Bus className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-stone-400 block text-xs font-bold uppercase tracking-wider">Transport & Bus</span>
                    <p className="text-brand-dark font-bold text-sm">Bus nr. 15 holder lige ved døren</p>
                    <span className="text-stone-400 text-xs block mt-0.5">Nemt og hurtigt at komme til fra hele Aalborg.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Google Maps Embed */}
            <div className="h-80 rounded-3xl overflow-hidden border border-stone-200 shadow-xl relative">
              <iframe
                title="Carinas Køreskole Gugvej"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2172.134563821012!2d9.9240898!3d57.0096284!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464939791def31d1%3A0x40126996!2sGugvej%20186D%2C%209210%20Aalborg%20S%C3%98!5e0!3m2!1sda!2sdk!4v1718010000000!5m2!1sda!2sdk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0"
              ></iframe>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 sm:p-8 flex gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-2 text-amber-900 text-sm leading-relaxed">
                <h4 className="font-bold text-brand-dark">Vigtige betingelser ved tilmelding</h4>
                <p className="text-xs text-amber-800">
                  <strong>OBS:</strong> Ved udeblivelse eller afmelding fra opstartsdato uden rettidigt afbud (senest 2 uger før) opkræves et gebyr på 3.000 kr.
                </p>
                <p className="text-xs text-amber-800">
                  Vælger du at stoppe midt i forløbet, refunderes det resterende beløb for lovpakken ikke.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
